// Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  Warning,
  SvgIcon,
  Button,
  NavLink,
  CardTable as ResponsibleIdentitiesTable
} from '../'

import { decodeBase64 } from '../../utils'

const QUERY_RESPONSIBILITIES = gql`
  query($payrollCycleId: UUID!){
    responsibilities(relatedId: $payrollCycleId) {
      edges {
        node {
          id
          identity {
            id
            fullName
          }
          description
        }
      }
    }
  }
`

const getRoleFriendlyText = (role) => (
  {
    'receives_deduction_files': 'Receiver',
    'deductions_approver_amounts': 'Amounts Approver',
    'deductions_approver_payment': 'Payment Approver',
    'deductions_editor': 'Amounts Editor',
    'deductions_reviewer': 'Reviewer'
  }[role]
)


export default class PayrollCycleAccessRights extends React.Component {
  static propTypes = {
    payrollCycleId: PropTypes.string.isRequired,
    className: PropTypes.string
  }

  static defaultProps = {
    className: ''
  }

  generateRows = (responsibilities) => {
    let identitiesWithRoles = {}

    /* first reorganise data in object with following shape:
    {
      [identity id]: {
        name: [identity name],
        role: [role]
      }
    }
    */
    responsibilities.forEach(({ node: responsibility }) => {
      const role = getRoleFriendlyText(responsibility.description)

      /*
      with each iteration check whether identity id is already stored,
      if yes, add to role (will only happen if an identity has multiple reponsibilities)
      if no, create entry in identityWithRoles object, using identity id as key to store identity name and role against
      */
      if (identitiesWithRoles.hasOwnProperty(responsibility.identity.id)) {
        let roles = identitiesWithRoles[responsibility.identity.id]['roles']
        if (roles.length === 0) {
          roles = role
        } else {
          roles += `, ${role}`
        }
        identitiesWithRoles[responsibility.identity.id]['roles'] = roles
      } else {
        identitiesWithRoles[responsibility.identity.id] = {}
        identitiesWithRoles[responsibility.identity.id]['name'] = responsibility.identity.fullName
        identitiesWithRoles[responsibility.identity.id]['roles'] = role
      }
    })

    const rows = Object.keys(identitiesWithRoles).map(id => (
      <tr key={`identitiesWithRoles_${id}`}>
        <td>
          <NavLink to={`/os/identity/${id}`}>
            {identitiesWithRoles[id].name}
          </NavLink>
        </td>
        <td>
          {identitiesWithRoles[id].roles}
        </td>
      </tr>
    ))

    return rows
  }

  render() {
    const { payrollCycleId, className } = this.props

    return (
      <Query query={QUERY_RESPONSIBILITIES} variables={{ payrollCycleId: decodeBase64(payrollCycleId) }}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Spinner type='tail' />
          }

          if (error) {
            return <Warning>Fetching identities with access to payroll cycle failed.</Warning>
          }

          if (data.responsibilities.edges.length === 0) {
            return (
              <div>
                <SvgIcon
                  variant='utility'
                  symbol='info'
                  size='small'
                  colour='light'
                  className='slds-m-right_small'
                />
                No identities with access to payroll cycle found.
              </div>
            )
          }

          const tableRows = this.generateRows(data.responsibilities.edges)

          return (
            <ResponsibleIdentitiesTable
              heading='Access rights'
              headingIcon='groups'
              columns={['Name', 'Roles']}
              tableDataRows={tableRows}
              actionButtons={[
                <Button
                  type='brand'
                  onClick={() => null}
                  minWidth={150}
                  disabled={true}
                  key='add'>
                  Add access rights
                </Button>
              ]}
              navigationButtons={false}
              className={className}
            />
          )
        }}
      </Query>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/payroll-cycle-access-rights/index.js